import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, Calendar, Users, CreditCard, CheckCircle, XCircle, Clock, Hotel, DoorOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { client } from '../api/client';
import { localizedName, localizedDescription, formatDate, formatDateTime } from '../lib/localized';

interface Payment {
  id: number;
  gateway: string;
  amount: number;
  currency: string;
  status: string;
  createdAt: number;
}

interface OrderData {
  id: number;
  checkIn: number;
  checkOut: number;
  guests: number;
  totalAmount: number;
  currency: string;
  status: string;
  paymentStatus: string;
  supplierStatus: string;

  paymentMethod: string | null;
  paymentReference: string | null;
  paidAt: number | null;
  confirmedAt: number | null;
  cancelledAt: number | null;
  customer?: {
    name: string | null;
    email: string;
    phone: string | null;
  } | null;
  property?: {
    name: string;
    nameEn?: string | null;
    nameZh?: string | null;
    nameZhCn?: string | null;
    description?: string | null;
    descriptionEn?: string | null;
    descriptionZh?: string | null;
    descriptionZhCn?: string | null;
    imageUrl: string | null;
    location: string | null;
  } | null;
  roomType?: {
    name: string;
    nameEn?: string | null;
    nameZh?: string | null;
    nameZhCn?: string | null;
  } | null;
  payments?: Payment[];
  addons?: string | null;
}

interface OrderAddon {
  name?: string | null;
  nameEn?: string | null;
  nameZh?: string | null;
  nameZhCn?: string | null;
  description?: string | null;
  descriptionEn?: string | null;
  descriptionZh?: string | null;
  descriptionZhCn?: string | null;
  type?: string | null;
}

export default function OrderPage() {
  const { t } = useTranslation('order');
  const { token } = useParams<{ token: string }>();
  const [order, setOrder] = useState<OrderData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const statusLabels = useMemo(() => ({
    pending: t('status.order.pending'),
    confirmed: t('status.order.confirmed'),
    cancelled: t('status.order.cancelled'),
    completed: t('status.order.completed'),
  }), [t]);

  const paymentStatusLabels = useMemo(() => ({
    unpaid: t('status.payment.unpaid'),
    partial: t('status.payment.partial'),
    paid: t('status.payment.paid'),
    refunded: t('status.payment.refunded'),
  }), [t]);

  const supplierStatusLabels = useMemo(() => ({
    pending: t('status.supplier.pending'),
    confirmed: t('status.supplier.confirmed'),
    rejected: t('status.supplier.rejected'),
  }), [t]);

  useEffect(() => {
    if (!token) {
      setError(t('error.incompleteLink'));
      setLoading(false);
      return;
    }
    async function load() {
      try {
        const res = await client.api.fetch(`/api/public/bookings/token/${encodeURIComponent(token)}`);
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || t('error.loadOrder'));
        }
        const json = await res.json();
        setOrder(json.data || null);
      } catch (err) {
        setError(err instanceof Error ? err.message : t('error.loadFailed'));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [token, t]);

  if (loading) {
    return (
      <div className="pt-32 pb-20 text-center text-gray-500">
        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" />
        <p>{t('loading')}</p>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="pt-32 pb-20 px-4 text-center">
        <div className="max-w-md mx-auto bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-[#0d1b2a] mb-2">{t('error.title')}</h1>
          <p className="text-gray-600">{error || t('error.notFound')}</p>
        </div>
      </div>
    );
  }

  const nights = Math.max(1, Math.round((order.checkOut - order.checkIn) / 86400));

  return (
    <div className="pt-24 pb-16 px-4 bg-[#f8fafb] min-h-screen">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden"
        >
          {/* Header */}
          <div className="bg-[#0a4c6b] text-white p-6 md:p-8">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-[#B8902F]" />
              <span className="text-white/80 text-sm">{t('header.subtitle')}</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold mb-1">{t('header.title', { id: order.id })}</h1>
            <p className="text-white/70 text-sm">{t('header.hint')}</p>
          </div>

          {/* Property */}
          {order.property && (
            <div className="p-6 md:p-8 border-b border-gray-100">
              <div className="flex flex-col md:flex-row gap-5">
                {order.property.imageUrl && (
                  <img
                    src={order.property.imageUrl}
                    alt={localizedName(order.property)}
                    className="w-full md:w-40 h-32 object-cover rounded-2xl"
                  />
                )}
                <div>
                  <p className="text-sm text-[#B8902F] font-medium mb-1">{localizedName(order.property)}</p>
                  <h2 className="text-xl font-bold text-[#0d1b2a]">{localizedName(order.property)}</h2>
                  {order.property.location && <p className="text-sm text-gray-500 mt-1">{order.property.location}</p>}
                  {order.roomType && (
                    <div className="flex items-center gap-2 mt-3 text-sm text-gray-600">
                      <DoorOpen className="w-4 h-4 text-[#2ec4b6]" />
                      <span>{localizedName(order.roomType)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Dates & Guests */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-[#2ec4b6] mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">{t('dates.checkIn')}</p>
                <p className="font-medium text-[#0d1b2a]">{formatDate(order.checkIn)}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-[#2ec4b6] mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">{t('dates.checkOut')}</p>
                <p className="font-medium text-[#0d1b2a]">{formatDate(order.checkOut)}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Users className="w-5 h-5 text-[#2ec4b6] mt-0.5" />
              <div>
                <p className="text-xs text-gray-500">{t('dates.guestsAndNights')}</p>
                <p className="font-medium text-[#0d1b2a]">{t('dates.guestsAndNightsValue', { guests: order.guests, nights })}</p>
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="p-6 md:p-8 border-b border-gray-100">
            <h3 className="font-bold text-[#0d1b2a] mb-4">{t('status.title')}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-[#f0f9f7] rounded-xl p-4 text-center">
                <p className="text-xs text-gray-500 mb-1">{t('status.orderLabel')}</p>
                <p className="font-bold text-[#0a4c6b]">{statusLabels[order.status] || order.status}</p>
              </div>
              <div className="bg-[#f0f9f7] rounded-xl p-4 text-center">
                <p className="text-xs text-gray-500 mb-1">{t('status.paymentLabel')}</p>
                <p className="font-bold text-[#0a4c6b]">{paymentStatusLabels[order.paymentStatus] || order.paymentStatus}</p>
              </div>
              <div className="bg-[#f0f9f7] rounded-xl p-4 text-center">
                <p className="text-xs text-gray-500 mb-1">{t('status.supplierLabel')}</p>
                <p className="font-bold text-[#0a4c6b]">{supplierStatusLabels[order.supplierStatus] || order.supplierStatus}</p>
              </div>
            </div>
          </div>

          {/* Customer */}
          <div className="p-6 md:p-8 border-b border-gray-100">
            <h3 className="font-bold text-[#0d1b2a] mb-4">{t('customer.title')}</h3>
            <div className="space-y-2 text-sm">
              <p><span className="text-gray-500">{t('customer.name')}</span>{order.customer?.name || '—'}</p>
              <p><span className="text-gray-500">{t('customer.email')}</span>{order.customer?.email || '—'}</p>
              <p><span className="text-gray-500">{t('customer.phone')}</span>{order.customer?.phone || '—'}</p>
            </div>
          </div>

          {/* Add-ons */}
          {(() => {
            const addonList = (() => {
              try { return JSON.parse(order.addons || '[]'); }
              catch { return []; }
            })();
            if (!addonList.length) return null;
            return (
              <div className="p-6 md:p-8 border-b border-gray-100">
                <h3 className="font-bold text-[#0d1b2a] mb-4">{t('addons.title')}</h3>
                <div className="space-y-2">
                  {addonList.map((a: OrderAddon, i: number) => (
                    <div key={i} className="flex items-center justify-between bg-[#f0f9f7] rounded-lg px-4 py-3 text-sm">
                      <div>
                        <p className="font-medium text-[#0a4c6b]">{localizedName(a) || t('addons.unnamed')}</p>
                        {localizedDescription(a) && <p className="text-gray-600 text-xs mt-0.5">{localizedDescription(a)}</p>}
                      </div>
                      {a.type && (
                        <span className="text-xs px-2 py-1 rounded-full bg-white text-[#0a4c6b]">
                          {a.type === 'retreat' ? t('addons.retreat') : t('addons.experience')}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Payment summary */}
          <div className="p-6 md:p-8">
            <h3 className="font-bold text-[#0d1b2a] mb-4">{t('payment.title')}</h3>
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600">{t('payment.total')}</span>
              <span className="text-xl font-bold text-[#0a4c6b]">HK${order.totalAmount.toLocaleString()}</span>
            </div>
            {order.paymentMethod && (
              <p className="text-sm text-gray-500 mb-4">{t('payment.method', { method: order.paymentMethod })}</p>
            )}
            {order.payments && order.payments.length > 0 && (
              <div className="space-y-2 mt-4">
                {order.payments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between text-sm bg-gray-50 rounded-lg px-4 py-2">
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-[#2ec4b6]" />
                      <span>{formatDate(p.createdAt)}</span>
                    </div>
                    <span className="font-medium">HK${p.amount.toLocaleString()} · {p.status}</span>
                  </div>
                ))}
              </div>
            )}

          </div>
        </motion.div>

        <p className="text-center text-gray-400 text-xs mt-8">
          {t('footer')}
        </p>
      </div>
    </div>
  );
}
